import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbsMenu, BreadcrumbsMenuItem, KNOWLEDGE_DESIGNER_ROOT_ROUTE } from '@selfai-platform/shell';
import { Observable, combineLatest, map, of, switchMap, take } from 'rxjs';
import { GUEST_WORKSPACE_ID, MY_WORKSPACE_ID } from '../../workspace/constants';
import { PublicType } from '../../workspace/enums';
import { Workspace } from '../../workspace/models';
import { WorkspaceDomainService } from '../../workspace/services/workspace-domain.service';
import { WorkbookDomainService } from './workbook-domain.service';

// Bread crumbs menu for dashboard list of workbook
@Injectable({
  providedIn: 'root',
})
export class WorkbookDashboardListBreadcrumbService implements BreadcrumbsMenu {
  constructor(
    private readonly workbookDomainService: WorkbookDomainService,
    private readonly workspaceDomainService: WorkspaceDomainService,
    private readonly translate: TranslateService,
  ) {}

  getBreadcrumbsMenu(workbookId: string): Observable<BreadcrumbsMenuItem[]> {
    return this.workbookDomainService
      .loadWorkbook(workbookId)
      .pipe(take(1))
      .pipe(
        switchMap((workbook) => {
          return combineLatest({
            workspace: this.workspaceDomainService.loadWorkspace(workbook.workspaceId),
            workbook: of(workbook),
            publicWorkspaceName: this.translate.stream('msg.workspace-list.toolbar.public-workspace'),
            guestWorkspaceName: this.translate.stream('msg.workspace-list.toolbar.guest-workspace'),
            myWorkspaceName: this.translate.stream('msg.workspace-list.toolbar.my-workspace'),
            knowledgeDesigner: this.translate.stream('shell.menu.knowledge-designer'),
          });
        }),

        map(({ workspace, workbook, ...translate }) => {
          const currentWorkspaceType = this.getWorkspaceType(workspace);
          let currentWorkspaceTypeItem: BreadcrumbsMenuItem;

          switch (currentWorkspaceType) {
            case 'guest':
              currentWorkspaceTypeItem = {
                label: translate.guestWorkspaceName,
                routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspace', GUEST_WORKSPACE_ID],
              };
              break;
            case 'my':
              currentWorkspaceTypeItem = {
                label: translate.myWorkspaceName,
                routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspace', MY_WORKSPACE_ID],
              };
              break;
            case 'public':
              currentWorkspaceTypeItem = {
                label: translate.publicWorkspaceName,
                routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspaces'],
              };
              break;
            default:
            case 'private':
              currentWorkspaceTypeItem = {
                label: workspace.name,
                routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspace', workspace.id],
                description: workspace.description,
              };
              break;
          }

          const workspaceListItems: BreadcrumbsMenuItem[] = [
            { label: translate.knowledgeDesigner, routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspaces'] },
            currentWorkspaceTypeItem,
          ];

          if (currentWorkspaceType === 'public') {
            workspaceListItems.push({
              label: workspace.name,
              icon: 'pi pi-briefcase',
              routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workspace', workspace.id],
              description: workspace.description,
            });
          }

          workspaceListItems.push({
            label: workbook.name,
            icon: 'pi pi-book',
            routerLink: ['/', KNOWLEDGE_DESIGNER_ROOT_ROUTE, 'workbook', workbook.id],
          });

          return workspaceListItems;
        }),
      );
  }

  private getWorkspaceType(workspace: Workspace): 'public' | 'my' | 'guest' | 'private' {
    if (workspace.id === GUEST_WORKSPACE_ID) {
      return 'guest';
    }

    if (workspace.id === MY_WORKSPACE_ID) {
      return 'my';
    }

    if (workspace.publicType === PublicType.SHARED) {
      return 'public';
    }

    return 'private';
  }
}
